<template>
  <div>
    <transition name="slideUp">
      <div class="upgradeContainer" v-if="open">
        <div class="upgradeWindow">
          <font-awesome-icon @click="$emit('close')" class="closeButton" :icon="['fas', 'xmark']" />

    <!-- Upgrade Content -->
    <div class="upgrade-prompt">
      <div class="upgrade-icon">
        <font-awesome-icon 
          :icon="['fas', iconType]" 
          :class="iconClass"
        />
      </div>
      
      <h2 class="upgrade-title">{{ title }}</h2>
      <p class="upgrade-message">{{ message }}</p>
      
      <div class="subscription-options" v-if="showUpgradeOptions">
        <div class="option-card" v-if="subscriptionType === 'free'">
          <h3>Device Subscription</h3>
          <p>Complete access to all network management features</p>
          <ul>
            <li>Filter & Blocker</li>
            <li>VPN Connection</li>
            <li>Site Manager</li>
            <li>Device Manager</li>
            <li>Network Stats</li>
            <li>Firewall Rules</li>
            <li>Port Forwarding</li>
            <li>AI Assistant</li>
          </ul>
          <div class="buttonContainer">
            <button class="secondaryButton" @click="upgradeToDevice">
              Upgrade to Device Plan
            </button>
          </div>
        </div>
      </div>

      <div class="device-status" v-if="showDeviceStatus">
        <div class="status-indicator" :class="{ 'offline': !deviceOnline }">
          <font-awesome-icon :icon="['fas', deviceOnline ? 'circle-check' : 'circle-xmark']" />
          <span>Device {{ deviceOnline ? 'Online' : 'Offline' }}</span>
        </div>
        <p v-if="!deviceOnline" class="device-help">
          Please check your device connection to access this feature.
        </p>
      </div>
    </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { useLoginStore } from '@/stores/loginStore'
import SubscriptionPermissionService from '@/services/SubscriptionPermissionService'

export default {
  name: 'UpgradePromptPage',
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    feature: {
      type: String,
      default: ''
    },
    returnPath: {
      type: String,
      default: '/'
    }
  },
  data() {
    return {
      loginStore: useLoginStore()
    }
  },
  computed: {
    featureName() {
      return this.feature || ''
    },
    subscriptionType() {
      return this.loginStore.getSubscriptionType
    },
    deviceOnline() {
      return this.loginStore.getDeviceOnline
    },
    isFreeTier() {
      return this.subscriptionType === 'free'
    },
    hasDeviceSubscription() {
      return this.subscriptionType === 'device'
    },
    accessLevel() {
      if (!this.featureName) return 'blocked'
      return SubscriptionPermissionService.getAccessLevel(
        this.subscriptionType,
        this.featureName,
        this.deviceOnline
      )
    },
    title() {
      if (this.customTitle) return this.customTitle
      
      switch (this.accessLevel) {
        case 'blocked':
          return 'Upgrade Required'
        case 'upgrade_required':
          return 'Device Subscription Required'
        case 'device_offline':
          return 'Device Connection Required'
        default:
          return 'Access Restricted'
      }
    },
    message() {
      if (this.customMessage) return this.customMessage
      
      if (this.featureName) {
        return SubscriptionPermissionService.getUpgradeMessage(
          this.subscriptionType,
          this.featureName
        )
      }
      
      return 'This feature is not available with your current subscription.'
    },
    iconType() {
      switch (this.accessLevel) {
        case 'device_offline':
          return 'wifi'
        case 'upgrade_required':
          return 'microchip'
        default:
          return 'lock'
      }
    },
    iconClass() {
      switch (this.accessLevel) {
        case 'device_offline':
          return 'icon-warning'
        case 'upgrade_required':
          return 'icon-premium'
        default:
          return 'icon-locked'
      }
    },
    showUpgradeOptions() {
      return this.subscriptionType === 'free' && (this.accessLevel === 'blocked' || this.accessLevel === 'upgrade_required')
    },
    showDeviceStatus() {
      return this.accessLevel === 'device_offline' || 
             (this.hasDeviceSubscription && this.featureName)
    }
  },
  methods: {
    goBack() {
      this.$emit('close')
    },
    upgradeToDevice() {
      this.$emit('close')
      this.$emit('close-menu')
      this.$router.push({ 
        name: 'billingSubscription'
      })
    }
  }
}
</script>

<style scoped>
.buttonContainer{
  display: flex;
  justify-content: center;
}
.closeButton {
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 1.5rem;
  color: var(--text-primary);
  cursor: pointer;
  z-index: 10;
  background: transparent;
  padding: 10px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.closeButton:hover {
  background: rgba(0, 0, 0, 0.2);
  transform: scale(1.1);
}

.upgradeContainer {
  width: 100vw;
  height: calc(100vh - var(--header-height));
  background-color: var(--color-background);
  position: absolute;
  top: var(--header-height);
  left: 0;
  z-index: 100;
}

.upgradeWindow {
  width: 100%;
  height: 100%;
  background-color: var(--color-card);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border: 1px solid var(--color-card-border);
  position: relative;
  overflow-y: auto;
}

/* slide-in/out animation */
.slideUp-enter-active,
.slideUp-leave-active {
  transition: transform .3s ease;
}
.slideUp-enter {
  transform: translateY(100%);
}
.slideUp-enter-to {
  transform: translateY(0);
}
.slideUp-leave {
  transform: translateY(0);
}
.slideUp-leave-to {
  transform: translateY(100%);
}

.upgrade-prompt {
  padding: 60px 20px 40px 20px;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  color: var(--text-primary);
}

.upgrade-icon {
  font-size: 80px;
  margin-bottom: 30px;
}

.icon-locked {
  color: #e74c3c;
}

.icon-premium {
  color: #f39c12;
}

.icon-warning {
  color: #f1c40f;
}

.upgrade-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 20px;
  color: var(--text-primary);
}

.upgrade-message {
  font-size: 1.125rem;
  color: var(--text-secondary);
  margin-bottom: 40px;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.subscription-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
}

.option-card {
  background: var(--color-card);
  border: 2px solid var(--color-card-border);
  border-radius: 16px;
  padding: 30px;
  text-align: left;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-card);
  color: var(--text-primary);
}

.option-card:hover {
  border-color: var(--color-secondary-button);
  transform: translateY(-4px);
  box-shadow: var(--shadow-hover);
}

.option-card h3 {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: var(--text-primary);
}

.option-card p {
  font-size: 1rem;
  color: var(--text-secondary);
  margin-bottom: 20px;
  line-height: 1.5;
}

.option-card ul {
  list-style: none;
  padding: 0;
  margin-bottom: 25px;
}

.option-card li {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 10px;
  padding-left: 25px;
  position: relative;
  line-height: 1.4;
}

.option-card li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: var(--color-secondary-button);
  font-weight: bold;
  font-size: 1rem;
}

.upgrade-btn {
  width: 100%;
  padding: 15px 25px;
  border: none;
  border-radius: 12px;
  font-size: 1.125rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upgrade-btn.primary {
  background: var(--color-secondary-button);
  color: var(--text-primary);
}

.upgrade-btn.primary:hover {
  background: var(--color-secondary-button-border);
  transform: translateY(-2px);
}

.upgrade-btn.premium {
  background: linear-gradient(135deg, #f39c12, #e67e22);
  color: white;
}

.upgrade-btn.premium:hover {
  background: linear-gradient(135deg, #e67e22, #d35400);
  transform: translateY(-2px);
}

.device-status {
  background: var(--color-card);
  border: 1px solid var(--color-card-border);
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 40px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

.status-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 1.125rem;
  font-weight: bold;
  color: #27ae60;
}

.status-indicator.offline {
  color: #e74c3c;
}

.device-help {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-top: 15px;
  margin-bottom: 0;
  text-align: center;
}

.page-actions {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.action-btn {
  padding: 15px 30px;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;
}

.action-btn.secondary {
  background: var(--color-card);
  border: 2px solid var(--color-card-border);
  color: var(--text-secondary);
}

.action-btn.secondary:hover {
  background: var(--color-background-input);
  color: var(--text-primary);
}

.action-btn.primary {
  background: var(--color-secondary-button);
  color: var(--text-primary);
}

.action-btn.primary:hover {
  background: var(--color-secondary-button-border);
  transform: translateY(-2px);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .upgrade-prompt {
    padding: 20px 15px;
  }
  
  .upgrade-icon {
    font-size: 60px;
    margin-bottom: 20px;
  }
  
  .upgrade-title {
    font-size: 1.75rem;
  }
  
  .subscription-options {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .option-card {
    padding: 20px;
  }
  
  .page-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .action-btn {
    width: 100%;
    max-width: 300px;
  }
}
</style>